import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import persist from '@alpinejs/persist';
import lottie from 'lottie-web';
import AOS from 'aos';
import { defineElement } from '@lordicon/element';
import './components/addresses';
import './components/filter';
import './components/header';
import './components/modal';
import './components/notifications';
import './components/productNav';
import './components/quickReplace';
import './components/sliders';
import './components/email-tracker';

// Register lord-icon immediately — custom elements need early registration
defineElement(lottie.loadAnimation);

AOS.init();
// ============================================
// SWIPER — lazy loaded
// ============================================
const loadSwiper = () => import(/* webpackChunkName: "swiper" */ 'swiper');

async function initSwipers() {
    // Check if any swiper elements exist on page
    const hasSwiper = document.querySelector(
        '.applications-slider, .markets-slider, .stories, .reviews-slider, ' +
        '.reviews-slider-mini, .hero-slider, .account-slider, .blogs-slider, ' +
        '.features-slider, .customers, .slider-autoplay, .slider-autoplay-reverse, ' +
        '.notification-slider'
    );
    if (!hasSwiper) return;

    const { default: Swiper, Autoplay, Navigation, Pagination } = await loadSwiper();
    Swiper.use([Autoplay, Navigation, Pagination]);

    function create(selector, config) {
        const el = document.querySelector(selector);
        return el ? new Swiper(el, config) : null;
    }

    create('.applications-slider', {
        spaceBetween: 12,
        breakpoints: {
            1200: { slidesPerView: 7, slidesPerGroup: 7 },
            992: { slidesPerView: 5, slidesPerGroup: 5 },
            768: { slidesPerView: 4, slidesPerGroup: 4 },
            576: { slidesPerView: 3, slidesPerGroup: 3 },
        },
    });

    create('.markets-slider', {
        spaceBetween: 12,
        breakpoints: {
            1200: { slidesPerView: 4.5, slidesPerGroup: 5.8 },
            992: { slidesPerView: 3.5, slidesPerGroup: 3 },
            768: { slidesPerView: 2.5, slidesPerGroup: 2 },
            576: { slidesPerView: 2, slidesPerGroup: 2 },
        },
    });

    create('.stories', {
        spaceBetween: 24, centeredSlides: true, loop: true,
        pagination: { el: '.swiper-pagination', clickable: true },
        breakpoints: {
            1200: { slidesPerView: 3.5, slidesPerGroup: 3 },
            992: { slidesPerView: 2.5, slidesPerGroup: 2 },
            768: { slidesPerView: 1.5, slidesPerGroup: 1 },
            576: { slidesPerView: 1, slidesPerGroup: 1 },
        },
    });

    create('.reviews-slider', {
        slidesPerView: 1, spaceBetween: 24,
        pagination: { el: '.swiper-pagination', clickable: true },
        breakpoints: { 660: { slidesPerView: 2, spaceBetween: 24 } },
    });

    create('.reviews-slider-mini', {
        slidesPerView: 1, loop: true,
        autoplay: { delay: 3000, disableOnInteraction: false },
    });

    create('.hero-slider', {
        slidesPerView: 1, centeredSlides: true, loop: true,
        pagination: { el: '.swiper-pagination', clickable: true },
    });

    create('.account-slider', {
        slidesPerView: 1, effect: 'fade', centeredSlides: true,
        pagination: { el: '.swiper-pagination', clickable: true },
    });

    create('.blogs-slider', {
        spaceBetween: 24,
        pagination: { el: '.swiper-pagination', clickable: true },
        navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
        breakpoints: {
            1200: { slidesPerView: 3, slidesPerGroup: 3 },
            700: { slidesPerView: 2, slidesPerGroup: 2 },
            480: { slidesPerView: 1, slidesPerGroup: 1 },
        },
    });

    create('.features-slider', {
        spaceBetween: 16, loop: true, speed: 2400, allowTouchMove: false,
        autoplay: { delay: 1000, disableOnInteraction: false },
        breakpoints: {
            1000: { slidesPerView: 3, slidesPerGroup: 3, loop: false },
            700: { slidesPerView: 1, slidesPerGroup: 1 },
        },
    });

    create('.customers', {
        spaceBetween: 24, loop: true,
        pagination: { el: '.swiper-pagination', clickable: true },
        breakpoints: {
            1200: { slidesPerView: 6, slidesPerGroup: 6 },
            992: { slidesPerView: 5, slidesPerGroup: 5 },
            768: { slidesPerView: 4, slidesPerGroup: 4 },
            576: { slidesPerView: 3, slidesPerGroup: 3 },
        },
    });

    const autoplayConfig = {
        spaceBetween: 24, centeredSlides: true, loop: true, speed: 5000,
        allowTouchMove: false, freeMode: true, pauseOnMouseEnter: true,
        breakpoints: {
            1200: { slidesPerView: 6 }, 992: { slidesPerView: 5 },
            768: { slidesPerView: 4 }, 576: { slidesPerView: 4 },
        },
    };

    create('.slider-autoplay', {
        ...autoplayConfig, autoplay: { delay: 0, disableOnInteraction: false },
    });

    create('.slider-autoplay-reverse', {
        ...autoplayConfig, autoplay: { delay: 0, disableOnInteraction: false, reverseDirection: true },
    });

    create('.notification-slider', {
        direction: 'vertical', slidesPerView: 1, loop: true, speed: 500,
        allowTouchMove: false, pauseOnMouseEnter: true,
        autoplay: { delay: 5000, reverseDirection: true, disableOnInteraction: false },
    });
}

// ============================================
// MOTOR IMAGE SWIPERS (htmx reinit)
// ============================================
let motorMain = null;
let motorThumbs = null;

async function initMotorSwipers() {
    const mainEl = document.querySelector('.slider-motor-images');
    const thumbEl = document.querySelector('.slider-motor-images-navigation');
    if (!mainEl || !thumbEl) return;

    const { default: Swiper, Thumbs } = await loadSwiper();
    Swiper.use([Thumbs]);

    if (motorThumbs) motorThumbs.destroy(true, true);
    if (motorMain) motorMain.destroy(true, true);

    motorThumbs = new Swiper(thumbEl, {
        spaceBetween: 8, slidesPerView: 5, watchSlidesProgress: true,
        breakpoints: { 768: { slidesPerView: 3 } },
    });

    motorMain = new Swiper(mainEl, {
        slidesPerView: 1, thumbs: { swiper: motorThumbs },
    });
}

// ============================================
// HOVER ZOOM
// ============================================
function initHoverZoom() {
    document.querySelectorAll('.hover-zoom-container:not([data-zoom-init])').forEach(container => {
        container.dataset.zoomInit = 'true';
        const img = container.querySelector('.zoom-image');
        if (!img) return;
        container.addEventListener('mouseenter', () => img.style.transform = 'scale(3)');
        container.addEventListener('mouseleave', () => img.style.transform = 'scale(1)');
        container.addEventListener('mousemove', (e) => {
            const rect = container.getBoundingClientRect();
            img.style.transformOrigin = `${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`;
        });
    });
}

// ============================================
// AUTOMA
// ============================================
function fetchDataFromAutoma() {
    const form = document.getElementById('automaForm-desktop');
    if (!form) return;

    fetch('/actions/automa-api/automa-api', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
    })
    .then(res => res.json())
    .then(data => {
        if (!data.success) {
            document.getElementById('automa-results-desktop').innerHTML = data.error;
            return;
        }
        form.style.display = 'none';
        document.getElementById('automa-results-desktop').innerHTML = data.html;

        document.querySelectorAll('.variant-radio').forEach(radio => {
            radio.addEventListener('change', function () {
                if (!this.checked) return;
                const condition = this.getAttribute('data-condition');
                const url = new URL(window.location.href);
                url.searchParams.set('condition', condition);
                window.history.pushState({}, '', url);
                const f = document.querySelector("[name='fields[condition]']");
                if (f) f.value = condition;
            });
        });

        document.querySelectorAll('.product-quote-button').forEach(btn => {
            btn.addEventListener('click', (e) => {
                MicroModal.show('product-quote');
                const q = document.querySelector('#product-quote');
                q.querySelector("[name='fields[brand]']").value = e.target.dataset.brand;
                q.querySelector("[name='fields[condition]']").value = e.target.dataset.condition;
                q.querySelector("[name='fields[partNumber]']").value = e.target.dataset.partnumber;
            });
        });
    })
    .catch(() => {
        document.getElementById('automa-results-desktop').innerHTML = '<p>Error fetching products.</p>';
    });
}

// ============================================
// CUSTOM FILE UPLOAD — show selected filenames
// ============================================
function initCustomUpload() {
    document.querySelectorAll('.custom-upload').forEach(wrapper => {
        const input = wrapper.querySelector('input[type="file"]');
        if (!input || input.dataset.uploadInit) return;
        input.dataset.uploadInit = 'true';

        const dt = new DataTransfer();

        function renderFileList() {
            let list = wrapper.querySelector('.upload-file-list');
            if (!list) {
                list = document.createElement('div');
                list.className = 'upload-file-list';
                wrapper.querySelector('.fui-field-container').appendChild(list);
            }

            list.innerHTML = '';
            if (dt.files.length === 0) return;

            Array.from(dt.files).forEach((file, index) => {
                const item = document.createElement('div');
                item.className = 'upload-file-item';

                const name = document.createElement('span');
                name.textContent = file.name;

                const removeBtn = document.createElement('button');
                removeBtn.type = 'button';
                removeBtn.className = 'upload-file-remove';
                removeBtn.textContent = '✕';
                removeBtn.setAttribute('aria-label', 'Remove ' + file.name);
                removeBtn.addEventListener('click', () => {
                    dt.items.remove(index);
                    input.files = dt.files;
                    renderFileList();
                });

                item.appendChild(name);
                item.appendChild(removeBtn);
                list.appendChild(item);
            });
        }

        input.addEventListener('change', () => {
            Array.from(input.files).forEach(file => dt.items.add(file));
            input.files = dt.files;
            renderFileList();
        });

        const form = input.closest('form');
        if (form) {
            form.addEventListener('submit', () => {
                const list = wrapper.querySelector('.upload-file-list');
                if (list) list.innerHTML = '';
            });
        }
    });
}

// ============================================
// BOOT
// ============================================
window.addEventListener('DOMContentLoaded', () => {
    LiveChatWidget.init();

    if (navigator.platform.indexOf('Win') > -1) {
        document.body.classList.add('windows-os');
    }

    // Fancybox — only if elements exist
    if (document.querySelector('[data-fancybox]')) {
        Fancybox.bind('[data-fancybox]', {
            Carousel: {
                Toolbar: { display: { left: [], middle: [], right: ['close'] } },
                Zoomable: {
                    Panzoom: {
                        maxScale: 'cover',
                        Panzoom: { clickAction: 'iterateZoom', maxScale: 5 },
                        mouseMoveFactor: 1.1,
                    },
                },
            },
        });
    }

    // Alpine — deferred
    (window.requestIdleCallback || (cb => setTimeout(cb, 1)))(() => {
        window.Alpine = Alpine;
        Alpine.plugin(collapse);
        Alpine.plugin(persist);
        Alpine.magic('clipboard', () => subject => navigator.clipboard.writeText(subject));
        Alpine.start();
    });

    // Custom file uploads
    initCustomUpload();

    // Swipers — lazy loaded
    initSwipers();
    initMotorSwipers();
    initHoverZoom();
    window.sliders();

    // Automa — on first interaction
    document.addEventListener('mousemove', () => fetchDataFromAutoma(), { once: true });
    document.addEventListener('touchmove', () => fetchDataFromAutoma(), { once: true });

    if (document.querySelector('.formie-form.calculator')) {
        // get container .fui-btn-wrapper element
        const container = document.querySelector('.formie-form.calculator .fui-btn-wrapper');
        if (container) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'btn btn-blue';
            btn.textContent = 'Reset';
            container.appendChild(btn);

            btn.addEventListener('click', () => {
                const form = container.closest('form');
                if (form) form.reset();
            });
        }
        

    }
});

htmx.on('htmx:afterSwap', () => {
    if (document.querySelector('.slider-motor-images')) initMotorSwipers();
    initHoverZoom();
});